import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, ListItemText, MenuItem, Select, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import UploadFile from '@mui/icons-material/UploadFile';
import api, { userGroupService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const labels = { physical: 'Ativos físicos', token: 'Tokens', license: 'Licenças', certificate: 'Certificados', domain: 'Domínios' };
const subcategories = ['Computadores', 'Monitores', 'Impressoras', 'Periféricos', 'Servidores', 'Consumíveis', 'Telefonia', 'Outro'];

export const downloadFile = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export default function ImportProducts({ section, subcategory: initialSubcategory = '' }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [formats, setFormats] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupIds, setGroupIds] = useState([]);
  const [format, setFormat] = useState('canonical');
  const [subcategory, setSubcategory] = useState(initialSubcategory);
  const [file, setFile] = useState(null);
  const [sheet, setSheet] = useState('');
  const [sheets, setSheets] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    setOpen(false);
    setSubcategory(initialSubcategory);
    setFile(null);
    setResult(null);
    setSuccess(null);
    setFormat('canonical');
  }, [section, initialSubcategory]);

  const begin = async () => {
    setOpen(true);
    setError('');
    setSuccess(null);
    try {
      const [formatResponse, groupResponse] = await Promise.all([
        api.get('/imports/formats'), userGroupService.getUserGroups(),
      ]);
      setFormats(formatResponse.data.formats.filter((entry) => entry.section === section));
      setGroups(groupResponse.groups || []);
    } catch (err) { setError(err.message); }
  };

  const invalidate = () => { setResult(null); setError(''); setSuccess(null); };
  const submit = async (operation) => {
    setBusy(true);
    setError('');
    try {
      const body = new FormData();
      body.append('file', file);
      body.append('format', format);
      body.append('permission_group_ids', JSON.stringify(groupIds));
      if (sheet) body.append('sheet', sheet);
      if (section === 'physical' && subcategory) body.append('subcategory', subcategory);
      if (operation === 'execute') body.append('receipt', result.receipt);
      const response = await api.post(`/imports/${section}/${operation}`, body, { timeout: 120000 });
      if (operation === 'execute') {
        setSuccess(response.data);
        setResult(null);
        window.dispatchEvent(new Event('inventory:imported'));
      } else {
        setResult(response.data);
        setSheets(response.data.sheets || []);
      }
    } catch (err) {
      setError(err.message);
      if (err.data?.errors) setResult({ valid: false, errors: err.data.errors });
    } finally { setBusy(false); }
  };

  const template = async () => {
    try {
      const response = await api.get(`/imports/${section}/template`, { responseType: 'blob' });
      downloadFile(response.data, `${section}_modelo.csv`);
    } catch (err) { setError(err.message); }
  };

  if (!['admin', 'manager'].includes(user?.role)) return null;
  return <>
    <Button variant="outlined" startIcon={<UploadFile />} onClick={begin}>Importar CSV/XLSX</Button>
    <Dialog open={open} onClose={() => !busy && setOpen(false)} maxWidth="lg" fullWidth>
      <DialogTitle>Importar — {labels[section]}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">Selecione a exportação da planilha atual ou use o modelo. Datas: DD/MM/AAAA ou AAAA-MM-DD. Valores: 1.234,56 ou 1234.56. CSV em UTF-8. Máximo: 10 MB e 5.000 produtos.</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField select fullWidth label="Formato das colunas" value={format} disabled={busy} onChange={(event) => { setFormat(event.target.value); invalidate(); }}>
              <MenuItem value="canonical">Modelo da seção</MenuItem>
              {formats.map((entry) => <MenuItem key={entry.id} value={entry.id}>{entry.label}</MenuItem>)}
            </TextField>
            <Button onClick={template}>Baixar modelo CSV</Button>
          </Stack>
          {section === 'physical' && <TextField select fullWidth label="Subcategoria para o lote (opcional)" value={subcategory} disabled={busy} onChange={(event) => { setSubcategory(event.target.value); invalidate(); }}>
            <MenuItem value="">Usar classificação de cada linha</MenuItem>
            {subcategories.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
          </TextField>}
          {format === 'imobilizados' && <Alert severity="info">Informe a subcategoria no arquivo ou selecione-a para o lote. Os campos obrigatórios de cada tipo continuam sendo validados. Owner e CC da planilha são preservados como informações adicionais; grupos de acesso são escolhidos abaixo.</Alert>}
          <FormControl fullWidth>
            <InputLabel id="import-groups-label">Grupos com acesso aos produtos</InputLabel>
            <Select multiple labelId="import-groups-label" label="Grupos com acesso aos produtos" value={groupIds} disabled={busy}
              renderValue={(ids) => groups.filter((group) => ids.includes(group.id)).map((group) => group.name).join(', ')}
              onChange={(event) => { setGroupIds(event.target.value); invalidate(); }}>
              {groups.filter((group) => group.is_active).map((group) => <MenuItem key={group.id} value={group.id}>
                <Checkbox checked={groupIds.includes(group.id)} /><ListItemText primary={group.name} />
              </MenuItem>)}
            </Select>
          </FormControl>
          <Button component="label" variant="outlined" disabled={busy}>
            {file ? file.name : 'Selecionar CSV ou XLSX'}
            <input type="file" accept=".csv,.xlsx" hidden onChange={(event) => {
              setFile(event.target.files[0] || null); setSheet(''); setSheets([]); invalidate();
            }} />
          </Button>
          {sheets.length > 1 && <TextField select fullWidth label="Aba do XLSX" value={sheet} disabled={busy} onChange={(event) => { setSheet(event.target.value); invalidate(); }}>
            {sheets.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
          </TextField>}
          {result?.warnings?.length > 0 && <Alert severity="warning">{result.warnings.slice(0, 10).join('; ')}{result.warnings.length > 10 ? ` (${result.warnings.length} avisos no total)` : ''}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success.inserted} {success.inserted === 1 ? 'produto inserido' : 'produtos inseridos'}. Lote: {success.batch_id}</Alert>}
          {result?.errors?.length > 0 && <Alert severity="error">Nenhum produto será inserido. Corrija os erros e valide novamente.
            <Box component="ul" sx={{ pl: 2 }}>{result.errors.slice(0, 50).map((entry, index) => <li key={index}>{entry.line ? `Linha ${entry.line}: ` : ''}{entry.field} — {entry.message}</li>)}</Box>
            {result.errors.length > 50 && `${result.errors.length} erros encontrados; exibindo os primeiros 50.`}
          </Alert>}
          {result?.preview?.length > 0 && <>
            <Typography>{result.row_count} {result.row_count === 1 ? 'produto validado' : 'produtos validados'}. Prévia das primeiras 20 linhas (credenciais omitidas).</Typography>
            <TableContainer><Table size="small"><TableHead><TableRow>
              {['Linha', 'Produto', 'Subcategoria', 'Status', 'Quantidade', 'Campos adicionais preservados'].map((label) => <TableCell key={label}>{label}</TableCell>)}
            </TableRow></TableHead><TableBody>{result.preview.map((row) => <TableRow key={row.line}>
              <TableCell>{row.line}</TableCell><TableCell>{row.name}</TableCell><TableCell>{row.subcategory || '—'}</TableCell>
              <TableCell>{{ active: 'Ativo', inactive: 'Inativo', maintenance: 'Manutenção' }[row.status] || row.status}</TableCell><TableCell>{row.quantity ?? row.number_licenses ?? 1}</TableCell>
              <TableCell>{row.additional_fields.join(', ') || '—'}</TableCell>
            </TableRow>)}</TableBody></Table></TableContainer>
          </>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, p: 2 }}>
        <Button disabled={busy} onClick={() => { setOpen(false); if (success) window.location.reload(); }}>{success ? 'Concluir e atualizar lista' : 'Fechar'}</Button>
        {!success && <Button disabled={busy || !file || !groupIds.length} onClick={() => submit('validate')}>{busy ? 'Processando…' : 'Validar arquivo'}</Button>}
        {!success && <Button variant="contained" disabled={busy || !result?.valid || !result?.receipt} onClick={() => submit('execute')}>Confirmar inserção de {result?.row_count || 0} {result?.row_count === 1 ? 'produto' : 'produtos'}</Button>}
      </DialogActions>
    </Dialog>
  </>;
}
